import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import PersonaAvatar from '../../components/PersonaAvatar'
import type { Persona } from '../../lib/types'

export default function CommunityDirectoryPage() {
  const { user } = useAuth()
  const slug = user?.communitySlug || ''
  const communityId = user?.communityId || `portal-${slug}`
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

  // Fetch community team personas via dedicated endpoint
  const { data: personas } = useQuery({
    queryKey: ['communityTeamPersonas', communityId],
    queryFn: async () => {
      // Try dedicated community personas endpoint first
      const ids = [...new Set([communityId, `portal-${slug}`])]
      for (const pid of ids) {
        try {
          const res = await fetch(`/api/communities/${pid}/personas`, { headers })
          if (!res.ok) continue
          const data = await res.json()
          const names: string[] = data.personas || (Array.isArray(data) ? data : [])
          if (names.length > 0) {
            // Fetch full persona info for each name
            const allRes = await fetch('/api/personas', { headers })
            const allPersonas = allRes.ok ? await allRes.json() as Persona[] : []
            const nameSet = new Set(names)
            return allPersonas.filter(p => nameSet.has(p.name))
          }
        } catch { /* try next */ }
      }
      return []
    },
    enabled: !!slug,
  })

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <h1 className="text-lg font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>The Neighborhood</h1>
      <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Your Team</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(personas || []).map(p => (
          <div
            key={p.name}
            className="flex items-center gap-4 p-4 rounded-lg"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
          >
            <PersonaAvatar name={p.name} size={44} avatarImage={!!p.avatar_image} />
            <div>
              <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{p.display_name || p.name}</div>
              <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{p.role || ''}</div>
            </div>
          </div>
        ))}
      </div>
      {(personas || []).length === 0 && (
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No team members assigned to this community</p>
      )}
    </div>
  )
}
